<template>
  <Modal :open="true" :border="false" width="large" :clear-icon="false" :classes="{ modalContent: 'w-full max-md:!h-auto' }" :backdrop-close="false">
    <template #body>
      <h1 class="text-xl font-semibold text-app-gray-900">Update your Organization details to continue</h1>
      <div class="text-sm text-app-gray-500 !mt-4">
        Experience the incredible benefits of teamwork, even when working solo or outside of a formal organization. You are required to have at least
        one organization, even if its for personal use.
      </div>
      <form class="flex flex-col gap-4 !mt-4">
        <div class="grid grid-cols-2 gap-5 mt-5">
          <div>
            <TextField
              v-model="displayName"
              label="Organization Name*"
              aria-label="Organization Name*"
              :error="v$.displayName.$error"
              :helper-text="v$.displayName.$error ? v$.displayName.$errors[0].$message : ' '"
              @input="v$.displayName.$validate()"
            />
            <Select
              v-model="industry"
              label="Industry*"
              aria-label="Industry*"
              placeholder="Select an industry"
              class="mt-3"
              :show-clear-all-icon="false"
              :error="v$.industry.$error"
              :helper-text="v$.industry.$error ? v$.industry.$errors[0]?.$message : ' '"
              :options="teamIndustryOptions"
              @on-select="v$.industry.$validate()"
            />
            <TextField
              v-if="industry === 'Others'"
              v-model="customIndustry"
              placeholder="Specify others"
              :error="v$.customIndustry.$error"
              :helper-text="v$.customIndustry.$error ? (v$.customIndustry.$errors[0].$message as string) : ' '"
              @input="v$.customIndustry.$validate()"
            />
          </div>
          <div>
            <Select
              v-model="organizationSize"
              label="Organization Size*"
              aria-label="Organization Size*"
              placeholder="Select an organization size"
              :error="v$.organizationSize.$error"
              :helper-text="v$.organizationSize.$error ? v$.organizationSize.$errors[0].$message : ' '"
              :options="organizationSizeOptions"
              @on-select="v$.organizationSize.$validate()"
            />
            <TextField
              v-model="organizationUrl"
              class="mt-3"
              label="Organization URL"
              aria-label="Organization URL"
              placeholder="e.g. https://acme.com"
              :error="v$.organizationUrl.$error"
              :helper-text="v$.organizationUrl.$error ? (v$.organizationUrl.$errors[0].$message as string) : ' '"
              @input="v$.organizationUrl.$validate()"
            />
          </div>
        </div>
        <div class="my-5">
          <Button class="max-sm:w-full" block :loading="sendingForm" :disabled="v$.$invalid || sendingForm" @click="updateOrganization">
            Update Organization
          </Button>
          <div v-if="error" class="mt-2 text-app-error">{{ error }}</div>
        </div>
      </form>
    </template>
  </Modal>
</template>

<script lang="ts">
import { Modal } from "@toruslabs/vue-components/Modal";
import useVuelidate from "@vuelidate/core";
import { helpers, requiredIf } from "@vuelidate/validators";
import { defineComponent } from "vue";

import { Button } from "@/components/Button";
import { Select } from "@/components/Select";
import { TextField } from "@/components/TextField";
import log from "@/helpers/log";
import { organizationUrlValidation, teamIndustryValidation, teamNameValidation, teamSizeValidation } from "@/helpers/validations";
import { teamModule, userModule } from "@/store";
import { ORGANIZATION_INDUSTRIES, ORGANIZATION_SIZES, ROUTES, TOAST_TYPES } from "@/utils/enums";
import { Team } from "@/utils/interface";

export default defineComponent({
  name: "UpdateOrganization",
  components: { Button, Modal, Select, TextField },
  props: {
    selectedTeam: {
      type: Object as () => Team,
      required: true,
    },
  },
  data() {
    return {
      teamIndustryOptions: ORGANIZATION_INDUSTRIES.map((industry) => ({
        name: industry,
        value: industry,
      })),
      organizationSizeOptions: ORGANIZATION_SIZES.map((size) => ({
        name: size,
        value: size,
      })),
      sendingForm: false,
      displayName: "",
      industry: "",
      customIndustry: "",
      organizationUrl: "",
      organizationSize: "",
      error: "",
      v$: useVuelidate(),
    };
  },
  created() {
    this.displayName = this.selectedTeam.display_name ?? "";
    this.organizationSize = this.selectedTeam.organization_size ?? "";
    this.organizationUrl = this.selectedTeam.organization_url ?? "";
    this.industry = this.selectedTeam.industry ?? "";
    if (this.industry && !ORGANIZATION_INDUSTRIES.includes(this.industry)) {
      this.customIndustry = this.industry;
      this.industry = "Others";
    }
  },
  validations() {
    return {
      displayName: teamNameValidation,
      industry: teamIndustryValidation,
      organizationSize: teamSizeValidation,
      organizationUrl: organizationUrlValidation,
      customIndustry: {
        required: helpers.withMessage(
          "This field is required",
          requiredIf(() => this.industry === "Others")
        ),
      },
    };
  },
  methods: {
    async updateOrganization() {
      this.sendingForm = true;
      this.error = "";
      try {
        const selectedTeamId = this.selectedTeam.id;
        const isNameUpdated = this.displayName.trim() !== this.selectedTeam.display_name;
        await teamModule.updateTeam({
          teamId: selectedTeamId,
          displayName: this.displayName.trim(),
          organizationSize: this.organizationSize,
          organizationUrl: this.organizationUrl,
          industry: this.industry === "Others" ? this.customIndustry : this.industry,
        });
        userModule.setToastMessage({ message: "Organization Updated Successfully", type: TOAST_TYPES.SUCCESS });

        if (isNameUpdated) {
          const updatedTeamName = teamModule.teamsGetter.find((team: Team) => team.id === selectedTeamId)?.name;
          this.$router.replace({ name: ROUTES.HOME, params: { teamName: updatedTeamName } });
        }
      } catch (error) {
        this.error = `Error: ${(error as { message: string }).message}`;
        log.error(this.error);
      } finally {
        this.sendingForm = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped></style>
